// @givmo/fundraisers-module — a campaign's Layers tab (FCM-WEB25, plan §FC-16; the mockups Jonathan ruled
// 2026-10-06 23:05 PDT). Built once for both consoles: the class enters only as the server's data, never as
// a branch here.
//
// Top to bottom: an invitation to join another campaign (the child's side, FCM-BE34); a top its funded
// layers keep from being deleted (FCM-BE31's fold, option A); a campaign above not yet published (F20); a
// parent that can never come back (Q3); the structure as ONE tree (revision 1): the top campaign first, each
// layer indented under its parent with a line down to it, this campaign marked, every row with its full total,
// donations, state and public page where the viewer may see them (Q4, D-FCM-26's private scope); what this
// campaign calls the campaigns inside it (FCM-WEB59); and the campaigns this one invited. The tree reads
// FCM-BE63's structure read, one node's page at a time.
// FCM-WEB33 (D-FCM-35): other organizations' campaigns asking to become this one's layers, above the tree;
// this campaign's own request to join another drawn where it would sit while it waits (frame 06), and
// its answer as a notice above the tree for 14 days (06c, 06d).
import { useState, type ReactNode } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  sdk,
  type OrgFundraiserOut,
  type P2pLayerNodeOut,
  type P2pLayerStructureOut,
  type P2pOwnLayerRequestOut,
} from '@givmo/api-client';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Icon,
  RowActions,
  cn,
  testIdProp,
  toast,
  type RowAction,
} from '@givmo/design-system';
import {
  ArrowRightLeft,
  CalendarRange,
  ChevronRight,
  CircleCheck,
  IdCard,
  Info,
  Plus,
  Star,
  Trash2,
} from 'lucide-react';
import { ConfirmDialog } from './confirm-dialog';
import { campaignOptions } from './campaign-queries';
import { FUNDRAISERS_ROUTES } from './fundraisers-routes';
import { FundraisersLoading, ModuleLink } from './fundraisers-state';
import { fundraiserStateLabel, fundraiserStateTone } from './fundraisers-decode';
import { PHONE_HIT, StateNotice } from './lifecycle-ui';
import { displayP2pDate, displayP2pMoney, teamStatusLabel } from './p2p-decode';
import { LAYERS_COPY as C, SERVER_WORDS } from './layers-copy';
import { layerNameLive, layerRefusal } from './layers-decode';
import {
  LAYER_PAGE,
  MAX_LAYER_DEPTH,
  invalidateLayerReads,
  layerInvitationsOptions,
  layerInvitesMadeOptions,
  layerNodePageOptions,
  layerParentOptions,
  layerStructureOptions,
  publicCampaignOptions,
} from './layers-queries';
import { AddAboveDialog, AddBelowDialog, CardLevelDialog, MoveDialog } from './layer-dialogs';
import { LayerInvitation } from './layer-invitation';
import { LayerNameCard } from './layer-name-card';
import { useLivePage } from './use-live-page';
import { CampaignRequests } from './join-requests';
import { REQUESTS_COPY as R } from './join-requests-copy';
import { invalidateRequestReads, ownLayerRequestOptions } from './join-requests-queries';
import { useFundraisersModule } from './fundraisers-context';
import { FeaturedDialog, FeaturedLine } from './featured-fundraiser';
import { SEASON_COPY as SC } from './season-setup-copy';
import {
  featuredCandidates,
  featuredLineOf,
  keepDayTogether,
  seasonLive,
  shownFirst,
} from './season-setup-decode';
import { pledgeTemplateOptions } from './season-setup-queries';

/** A campaign an act is aimed at: this one, or another row of the tree. */
export interface LayerTarget {
  id: string;
  name: string;
}

type LayerDialog =
  | { kind: 'below'; target: LayerTarget }
  | { kind: 'above'; target: LayerTarget }
  | { kind: 'move'; layer: P2pLayerNodeOut; from: LayerTarget }
  | { kind: 'remove'; layer: P2pLayerNodeOut; from: LayerTarget }
  | { kind: 'leave'; parent: string }
  | { kind: 'revoke'; inviteId: string; layer: string }
  | { kind: 'card' }
  | { kind: 'withdraw'; request: P2pOwnLayerRequestOut }
  | { kind: 'featured' }
  | null;

/** FCM-WEB34 (decision 1): "Set up season" on a campaign with nothing inside it, "Add games" on a
 * season set up; null where neither is offered. */
export type SeasonAct = 'setup' | 'more' | null;

/** Why a top can't be deleted while its layers are funded (FCM-BE31's fold, option A; FCM-BE88). */
export type KeptBy = 'draft' | 'deactivated' | null;

const nameOf = (node: P2pLayerNodeOut) => node.campaign_name ?? C.anotherCampaign;

/** The tab's one "not here" state: its reads failed, or there is no tree to read (not served). */
export function LayersUnavailable({ onRetry }: { onRetry: () => void }) {
  return (
    <EmptyState
      testId="layers-error"
      title="Campaign management isn’t available"
      description="The campaign could not be loaded."
      action={
        <Button appearance="saas" tone="secondary" testId="layers-retry" onClick={onRetry}>
          Try again
        </Button>
      }
    />
  );
}

export function LayersTab({
  row,
  canManage,
  enabled,
  unavailable = false,
  endedBy = null,
  keptBy,
  leaveInStateLine,
  selfCharities,
  onChanged,
}: {
  row: OrgFundraiserOut;
  /** The moderation rank (OWNER) with the module's write grant: the tree's acts. */
  canManage: boolean;
  enabled: boolean;
  /** The tab can't serve here: not a campaign, or the backend serves no layers (dark). */
  unavailable?: boolean;
  /** A draft a campaign above ended (FCM-BE35): that campaign's name, and the draft reads ended. */
  endedBy?: string | null;
  keptBy: KeptBy;
  /** The page's state line already offers Leave (a layer the end of a gone parent closed). */
  leaveInStateLine: boolean;
  /** The names of this campaign's own charities, for the add dialogs' charities line. */
  selfCharities: readonly string[];
  onChanged: () => Promise<void>;
}) {
  const client = useQueryClient();
  const live = useLivePage();
  const parent = useQuery({ ...layerParentOptions(row.id), enabled, retry: false });
  const structure = useQuery({ ...layerStructureOptions(row.id), enabled, retry: false });
  const overview = useQuery({ ...campaignOptions(row.id), enabled, retry: false });
  const made = useQuery({ ...layerInvitesMadeOptions(row.id), enabled, retry: false });
  const invitations = useQuery({ ...layerInvitationsOptions(row.id), enabled, retry: false });
  // FCM-WEB33: this campaign's own request to join another, waiting or lately answered.
  const own = useQuery({ ...ownLayerRequestOptions(row.id), enabled, retry: false });
  const module = useFundraisersModule();
  // FCM-WEB34: the season setup, its template and a game's featured fundraiser serve together.
  const seasonOn = seasonLive(overview.data?.config);
  const template = useQuery({
    ...pledgeTemplateOptions(row.id),
    enabled: enabled && seasonOn && canManage,
    retry: false,
  });
  // What this campaign's page shows first (FCM-BE121), and the fundraisers that carry its pledges.
  const shown = useQuery({
    ...publicCampaignOptions(row.id),
    enabled: enabled && seasonOn,
    retry: false,
  });
  const [dialog, setDialog] = useState<LayerDialog>(null);
  // Closed, or a draft a campaign above ended: its structure is frozen either way (FCM-BE31's FROZEN).
  const ended = row.state === 'closed' || endedBy !== null;
  // A layer is added to a draft or open campaign only (FCM-BE31's states).
  const writable = canManage && !ended && (row.state === 'draft' || row.state === 'active');
  // A season is a campaign whose pledge template names whom it counts (D-FCM-53: such a pledge goes on
  // its own games, never on a page someone starts by joining). A team's template about each joiner's own
  // count isn't one, so its campaign is offered no season acts.
  const seasonTemplate =
    template.isSuccess &&
    template.data !== null &&
    (template.data.measure.subject_name ?? '').trim() !== '';

  async function reread() {
    await Promise.all([invalidateLayerReads(client), invalidateRequestReads(client), onChanged()]);
  }
  function refused(error: unknown) {
    if (!live()) return;
    const refusal = layerRefusal(error);
    toast.error(refusal.message);
    if (refusal.stale) {
      setDialog(null);
      void reread();
    }
  }
  const remove = useMutation({
    mutationFn: ({ layer, from }: { layer: P2pLayerNodeOut; from: LayerTarget }) =>
      sdk.campaignsRemoveCampaignLayer({
        path: { fundraiser_id: from.id, child_campaign_id: layer.campaign_id },
        throwOnError: true,
      }),
    onSuccess: async () => {
      await reread();
      if (live()) {
        setDialog(null);
        toast.success(C.layerRemoved);
      }
    },
    onError: refused,
  });
  // The parent's name rides as the variable, for the toast once it has gone.
  const leave = useMutation<unknown, Error, string>({
    mutationFn: () =>
      sdk.campaignsLeaveCampaignLayer({ path: { fundraiser_id: row.id }, throwOnError: true }),
    onSuccess: async (_data, parentName) => {
      await reread();
      if (live()) {
        setDialog(null);
        toast.success(C.leftToast(parentName));
      }
    },
    onError: refused,
  });
  const withdraw = useMutation({
    mutationFn: () =>
      sdk.campaignsWithdrawCampaignLayerRequest({
        path: { fundraiser_id: row.id },
        throwOnError: true,
      }),
    onSuccess: async () => {
      await reread();
      if (live()) {
        setDialog(null);
        toast.success(R.withdrawnToast);
      }
    },
    onError: refused,
  });
  const revoke = useMutation({
    mutationFn: (inviteId: string) =>
      sdk.campaignsRevokeCampaignLayerInvite({
        path: { fundraiser_id: row.id, invite_id: inviteId },
        throwOnError: true,
      }),
    onSuccess: async () => {
      await reread();
      if (live()) {
        setDialog(null);
        toast.success('Invitation revoked');
      }
    },
    onError: refused,
  });

  // Not served (a plain fundraiser, or layers dark): its reads never run, so it would load forever.
  if (unavailable) return <LayersUnavailable onRetry={() => void onChanged()} />;
  if (structure.isPending || parent.isPending) return <FundraisersLoading />;
  if (!structure.isSuccess || !parent.isSuccess)
    return (
      <LayersUnavailable
        onRetry={() => {
          void structure.refetch();
          void parent.refetch();
        }}
      />
    );
  const where = parent.data;
  const direct = where.ancestors[where.ancestors.length - 1] ?? null;
  const tree = structure.data;
  const above = tree.path.length > 1 ? tree.path[tree.path.length - 2]! : null;
  const pending = (invitations.data ?? []).filter((invite) => invite.status === 'pending');
  // An accepted invitation's campaign is in the tree, and a revoked one was this campaign's own act: the
  // card keeps the ones still waiting, and the answers that left no layer (frame 02's declined row).
  const invitedRows = (made.data ?? []).filter(
    (invite) => invite.status !== 'accepted' && invite.status !== 'revoked',
  );
  const config = overview.data?.config ?? null;
  // FCM-WEB33: campaigns' requests show while this campaign takes them or some still wait.
  const requestsShown =
    config?.join_policy === 'request_to_join' ||
    (overview.data?.pendingRequests?.campaigns ?? 0) > 0;
  const ownRequest = own.data ?? null;
  // While it waits, the tree draws the campaign it asked where it would sit (frame 06); "Add a layer
  // above" goes, since a campaign has one parent and asks one at a time.
  const pendingParent = ownRequest?.status === 'pending' ? ownRequest : null;
  const ownNotice = ownRequest ? ownRequestNotice(ownRequest) : null;
  // Decision 1: "Set up season" while nothing is inside this campaign (and no template, or a season's,
  // stands in the way), "Add games" once a season set up has games under it.
  const inside = (tree.path[tree.path.length - 1]!.layer_count ?? 0) > 0;
  const seasonAct: SeasonAct =
    !writable || !seasonOn || !template.isSuccess
      ? null
      : inside
        ? seasonTemplate
          ? 'more'
          : null
        : template.data === null || seasonTemplate
          ? 'setup'
          : null;
  // D-FCM-56: what this campaign's page shows first, said under its tree.
  const featuredEcho = config?.featured_fundraiser_id ?? null;
  const featuredShown = shownFirst(shown.data);
  const children = tree.rows.filter((node) => node.parent_campaign_id === row.id);
  const featuredLine = seasonOn
    ? featuredLineOf(featuredEcho, featuredShown, (id) =>
        keepDayTogether(
          children.find((node) => node.campaign_id === id)?.campaign_name ?? C.anotherCampaign,
        ),
      )
    : null;

  return (
    // Phone: no word left alone at a block's end (tester-5's item 12).
    <div className="flex min-w-0 flex-col gap-20 text-pretty" data-testid="page-layers">
      {pending.map((invite) => (
        <LayerInvitation
          key={invite.id}
          invite={invite}
          campaignId={row.id}
          canAnswer={canManage}
          onAnswered={reread}
        />
      ))}
      {overview.isSuccess && (
        <CampaignRequests
          campaignId={row.id}
          campaignName={row.name}
          canAnswer={canManage && !ended}
          enabled={enabled && requestsShown}
        />
      )}
      {ownNotice && (
        <StateNotice
          icon={ownRequest?.status === 'approved' ? CircleCheck : Info}
          testId={`layers-own-request-${ownRequest?.status}-notice`}
          action={null}
        >
          {ownNotice}
        </StateNotice>
      )}
      {keptBy && (
        <StateNotice icon={Info} testId="layers-kept-notice" action={null}>
          {keptBy === 'draft' ? C.draftTopNotice : C.deactivatedTopNotice}
        </StateNotice>
      )}
      {/* F20: under a campaign not yet published, this one's total counts publicly in nothing above it.
          Not under a parent that can never come back: its Leave notice speaks there. */}
      {tree.unpublished_above && !where.can_leave && (
        <StateNotice icon={Info} testId="layers-unpublished-above-notice" action={null}>
          {C.unpublishedAbove(tree.unpublished_above.campaign_name)}
        </StateNotice>
      )}
      {direct && where.can_leave && !leaveInStateLine && (
        <StateNotice
          icon={Info}
          testId="layers-leave-notice"
          action={
            canManage ? (
              <Button
                appearance="saas"
                tone="secondary"
                className={PHONE_HIT}
                onClick={() => setDialog({ kind: 'leave', parent: direct.campaign_name })}
                testId="layers-leave"
              >
                {C.leaveAction}
              </Button>
            ) : null
          }
        >
          {C.leaveNotice(direct.campaign_name)}
        </StateNotice>
      )}
      <StructureCard
        row={row}
        campaignId={row.id}
        tree={tree}
        enabled={enabled}
        writable={writable}
        // F19: the tree's acts are this campaign's owners' (with the module's write grant), whatever the
        // server says of another row: its dialogs mount only for them, and the view-only line says so.
        canAct={canManage}
        frozen={ended}
        keptBy={keptBy}
        frozenLine={
          // The server's FROZEN sentence names the campaign that ended: this one, or the one above it.
          ended && (tree.path[tree.path.length - 1]!.layer_count ?? 0) > 0
            ? SERVER_WORDS.frozen(endedBy ?? row.name)
            : null
        }
        // F19: a co-organizer below the owner rank reads the tree and changes nothing.
        viewOnlyLine={!canManage && !ended ? C.editorLine : null}
        canChangeCard={canManage && !ended && tree.path.length > 1}
        // The server's own sentence, where this organization's campaign sits under another
        // organization's and may not leave it (frame 02c).
        footnote={
          above && !above.ours && !where.can_leave
            ? SERVER_WORDS.removeOnlyByParent(nameOf(above))
            : null
        }
        pendingParent={pendingParent}
        onWithdraw={canManage ? (request) => setDialog({ kind: 'withdraw', request }) : null}
        onAddBelow={(target) => setDialog({ kind: 'below', target })}
        onAddAbove={(target) => setDialog({ kind: 'above', target })}
        onMove={(layer, from) => setDialog({ kind: 'move', layer, from })}
        onRemove={(layer, from) => setDialog({ kind: 'remove', layer, from })}
        onChangeCard={() => setDialog({ kind: 'card' })}
        seasonAct={seasonAct}
        onSeason={() => module.onNavigate(FUNDRAISERS_ROUTES.season(row.id))}
        featuredOn={seasonOn}
        featuredLine={featuredLine}
        canChangeFeatured={canManage && !ended}
        onChangeFeatured={() => setDialog({ kind: 'featured' })}
      />
      {/* FCM-WEB59 (D-FCM-52): what this campaign calls the campaigns inside it, under the tree that lists
          them, wherever the name is served, a campaign with nothing inside it yet included (decision 5).
          The name is one of the campaign's settings, so it follows the Campaign tab's rule for them (any
          campaign that hasn't ended, a deactivated one included), not the tree's draft-or-open one. */}
      {config && layerNameLive(config) && (
        <LayerNameCard
          campaignId={row.id}
          config={config}
          editable={canManage && !ended}
          onSaved={() => client.invalidateQueries({ queryKey: campaignOptions(row.id).queryKey })}
        />
      )}
      {invitedRows.length > 0 && (
        <Card flush testId="layers-invited">
          <CardHeader title={C.invitedTitle} subtitle={C.invitedSubtitle} />
          <div className="px-20 py-16">
            <ul className="divide-y divide-border-subtle">
              {invitedRows.map((invite) => (
                <li
                  key={invite.id}
                  className="flex flex-col gap-8 py-12 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:gap-12"
                  data-testid={`layers-invited-${invite.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-s14 font-w600 text-text-strong">
                      {invite.child_campaign_name ?? C.anotherCampaign}
                    </p>
                    <p className="mt-4 text-s12 text-text-secondary">
                      {invite.created_at ? C.invitedOn(displayP2pDate(invite.created_at)) : ''}
                      {invite.status === 'pending'
                        ? `${invite.created_at ? ' · ' : ''}Expires ${displayP2pDate(invite.expires_at)}`
                        : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-12">
                    <Badge tone="neutral">{teamStatusLabel(invite.status)}</Badge>
                    {invite.status === 'pending' && canManage && (
                      <Button
                        appearance="saas"
                        tone="ghost"
                        className={PHONE_HIT}
                        onClick={() =>
                          setDialog({
                            kind: 'revoke',
                            inviteId: invite.id,
                            layer: invite.child_campaign_name ?? C.anotherCampaign,
                          })
                        }
                        testId={`layers-invited-revoke-${invite.id}`}
                      >
                        Revoke
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </Card>
      )}
      {canManage && (
        <>
          <AddBelowDialog
            target={dialog?.kind === 'below' ? dialog.target : null}
            self={{ id: row.id, name: row.name }}
            config={config}
            selfCharities={selfCharities}
            seasonTemplate={seasonTemplate}
            onClose={() => setDialog(null)}
            onDone={reread}
          />
          <AddAboveDialog
            target={dialog?.kind === 'above' ? dialog.target : null}
            self={{ id: row.id, name: row.name }}
            config={config}
            selfCharities={selfCharities}
            onClose={() => setDialog(null)}
            onDone={reread}
          />
          <MoveDialog
            move={dialog?.kind === 'move' ? { layer: dialog.layer, from: dialog.from } : null}
            onClose={() => setDialog(null)}
            onDone={reread}
          />
          <CardLevelDialog
            open={dialog?.kind === 'card'}
            campaignId={row.id}
            tree={tree}
            chosen={config?.card_campaign_id ?? null}
            onClose={() => setDialog(null)}
            onDone={reread}
          />
          {featuredLine !== null && (
            <FeaturedDialog
              open={dialog?.kind === 'featured'}
              gameId={row.id}
              chosen={featuredEcho}
              candidates={featuredCandidates(row.id, children, shown.data, [
                featuredEcho,
                featuredShown,
              ])}
              onClose={() => setDialog(null)}
              onDone={async () => {
                await Promise.all([
                  reread(),
                  client.invalidateQueries({ queryKey: campaignOptions(row.id).queryKey }),
                  client.invalidateQueries({ queryKey: publicCampaignOptions(row.id).queryKey }),
                ]);
              }}
            />
          )}
          <ConfirmDialog
            open={dialog?.kind === 'remove'}
            onOpenChange={(open) => {
              if (!open && !remove.isPending) setDialog(null);
            }}
            title={C.removeTitle}
            body={
              dialog?.kind === 'remove'
                ? dialog.layer.ours
                  ? C.removeBody(nameOf(dialog.layer), dialog.from.name)
                  : C.removeBodyOther(nameOf(dialog.layer), dialog.from.name)
                : ''
            }
            confirmLabel={C.removeConfirm}
            tone="danger"
            testId="layers-remove-dialog"
            confirmTestId="layers-remove-confirm"
            busy={remove.isPending}
            onConfirm={() => {
              if (dialog?.kind === 'remove' && !remove.isPending)
                remove.mutate({ layer: dialog.layer, from: dialog.from });
            }}
          />
          <ConfirmDialog
            open={dialog?.kind === 'leave'}
            onOpenChange={(open) => {
              if (!open && !leave.isPending) setDialog(null);
            }}
            title={dialog?.kind === 'leave' ? C.leaveTitle(dialog.parent) : ''}
            body={dialog?.kind === 'leave' ? C.leaveBody(dialog.parent) : ''}
            confirmLabel={C.leaveAction}
            tone="danger"
            testId="layers-leave-dialog"
            confirmTestId="layers-leave-confirm"
            busy={leave.isPending}
            onConfirm={() => {
              if (dialog?.kind === 'leave' && !leave.isPending) leave.mutate(dialog.parent);
            }}
          />
          <ConfirmDialog
            open={dialog?.kind === 'withdraw'}
            onOpenChange={(open) => {
              if (!open && !withdraw.isPending) setDialog(null);
            }}
            title={R.withdrawTitle}
            body={
              dialog?.kind === 'withdraw'
                ? R.withdrawBody(dialog.request.parent_campaign_name ?? C.anotherCampaign)
                : ''
            }
            confirmLabel={R.withdraw}
            tone="danger"
            testId="layers-withdraw-dialog"
            confirmTestId="layers-withdraw-confirm"
            busy={withdraw.isPending}
            onConfirm={() => {
              if (dialog?.kind === 'withdraw' && !withdraw.isPending) withdraw.mutate();
            }}
          />
          <ConfirmDialog
            open={dialog?.kind === 'revoke'}
            onOpenChange={(open) => {
              if (!open && !revoke.isPending) setDialog(null);
            }}
            title="Revoke this invitation?"
            body={dialog?.kind === 'revoke' ? C.revokeBody(dialog.layer) : ''}
            confirmLabel="Revoke invitation"
            tone="danger"
            testId="layers-revoke-dialog"
            confirmTestId="layers-revoke-confirm"
            busy={revoke.isPending}
            onConfirm={() => {
              if (dialog?.kind === 'revoke' && !revoke.isPending) revoke.mutate(dialog.inviteId);
            }}
          />
        </>
      )}
    </div>
  );
}

/**
 * What came of this campaign's own request to join another, for the notice above the tree (frames 06c and
 * 06d; the other answers' lines are copy only). A withdrawn request was this campaign's own act, and one
 * cancelled because this campaign ended or joined another needs no notice: its tree says so.
 */
function ownRequestNotice(request: P2pOwnLayerRequestOut): string | null {
  const parent = request.parent_campaign_name ?? C.anotherCampaign;
  const answered = displayP2pDate(request.answered_at ?? request.created_at);
  switch (request.status) {
    case 'approved':
      return R.approvedNotice(parent, answered);
    case 'declined':
      return R.declinedNotice(parent, answered);
    case 'expired':
      return R.expiredNotice(parent, displayP2pDate(request.expires_at));
    case 'cancelled':
      return request.cancel_reason === 'parent_ended' ? R.cancelledNotice(parent) : null;
    default:
      return null;
  }
}

/**
 * A Leave the page's state line offers (frame 07c: a layer the end of a parent no one can bring back
 * closed, so leaving is its way back, ruled option C).
 */
export function LeaveDialogHost({
  campaignId,
  parentName,
  open,
  onClose,
  onDone,
}: {
  campaignId: string;
  parentName: string;
  open: boolean;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const client = useQueryClient();
  const live = useLivePage();
  const leave = useMutation({
    mutationFn: () =>
      sdk.campaignsLeaveCampaignLayer({ path: { fundraiser_id: campaignId }, throwOnError: true }),
    onSuccess: async () => {
      await Promise.all([invalidateLayerReads(client), onDone()]);
      if (live()) {
        onClose();
        toast.success(C.leftToast(parentName));
      }
    },
    onError: (error) => {
      if (!live()) return;
      const refusal = layerRefusal(error);
      toast.error(refusal.message);
      if (refusal.stale) {
        onClose();
        void onDone();
      }
    },
  });
  return (
    <ConfirmDialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !leave.isPending) onClose();
      }}
      title={C.leaveTitle(parentName)}
      body={C.leaveBody(parentName)}
      confirmLabel={C.leaveAction}
      tone="danger"
      testId="layers-leave-dialog"
      confirmTestId="layers-leave-confirm"
      busy={leave.isPending}
      onConfirm={() => {
        if (!leave.isPending) leave.mutate();
      }}
    />
  );
}

// ── The tree ──────────────────────────────────────────────────────────────────────────────────
//
// Geometry: a row at depth d centres its chevron (or its anchor dot) at x(d) = pad + (d − 1)·step + 10px
// and starts its name at x(d) + 16px. Each row draws its own pieces of line, full height, so the lines run
// unbroken down the list: the rails of the campaigns above it whose layers continue below it, its elbow off
// its parent's line (├, or └ for the last), and its own line down to its first layer when it is open. The
// steps are smaller on a phone (18px) than on a desktop (24px), so four levels fit at 390 with the deepest
// name still readable.

const LINE = 'border-icon-muted';
/** The middle of a row's first line, from its top: 12px of padding and a 20px line. */
const MID = 22;
const at = (depth: number, extra = 0) =>
  `calc(var(--pad) + ${depth - 1} * var(--step) + ${10 + extra}px)`;

/** How a campaign's row meets the lines: a chevron (it opens and closes), a dot (always open: the way
 * down to this campaign, or a campaign whose layers the read opened), or nothing (no layers under it). */
type Shape = 'toggle' | 'anchor' | 'leaf';

type Line =
  | {
      kind: 'node';
      node: P2pLayerNodeOut;
      depth: number;
      rails: boolean[];
      last: boolean;
      parent: P2pLayerNodeOut | null;
      shape: Shape;
      /** FCM-WEB33: the top, under the campaign it asked to join: its line in is dashed. */
      pendingAbove?: boolean;
    }
  | { kind: 'more'; parent: P2pLayerNodeOut; depth: number; rails: boolean[]; remaining: number }
  | { kind: 'fold'; parent: P2pLayerNodeOut; depth: number; rails: boolean[]; count: number }
  | { kind: 'empty'; depth: number; rails: boolean[] };

function StructureCard({
  row,
  campaignId,
  tree,
  enabled,
  writable,
  canAct,
  frozen: selfFrozen,
  keptBy,
  frozenLine,
  viewOnlyLine,
  canChangeCard,
  footnote,
  pendingParent,
  onWithdraw,
  onAddBelow,
  onAddAbove,
  onMove,
  onRemove,
  onChangeCard,
  seasonAct = null,
  onSeason,
  featuredOn = false,
  featuredLine = null,
  canChangeFeatured = false,
  onChangeFeatured,
}: {
  row: OrgFundraiserOut;
  campaignId: string;
  tree: P2pLayerStructureOut;
  enabled: boolean;
  writable: boolean;
  /** Whether this person may act from this campaign's tree at all (its owner rank and write grant). */
  canAct: boolean;
  /** This campaign has ended, or a campaign above ended it: nothing under it changes. */
  frozen: boolean;
  keptBy: KeptBy;
  frozenLine: string | null;
  viewOnlyLine: string | null;
  canChangeCard: boolean;
  footnote: string | null;
  /** FCM-WEB33: the campaign this one asked to join, while it waits; the tree shows where it would sit. */
  pendingParent: P2pOwnLayerRequestOut | null;
  /** Withdraw that request; null for a person who reads the tree only. */
  onWithdraw: ((request: P2pOwnLayerRequestOut) => void) | null;
  onAddBelow: (target: LayerTarget) => void;
  onAddAbove: (target: LayerTarget) => void;
  onMove: (layer: P2pLayerNodeOut, from: LayerTarget) => void;
  onRemove: (layer: P2pLayerNodeOut, from: LayerTarget) => void;
  onChangeCard: () => void;
  /** FCM-WEB34 (decision 1): the season act this campaign offers, and where it goes. */
  seasonAct?: SeasonAct;
  onSeason?: () => void;
  /** D-FCM-56: games' featured fundraisers serve here (the pledge engine and the layers are on). */
  featuredOn?: boolean;
  /** What this campaign's page shows first, said; null where it shows none. */
  featuredLine?: string | null;
  canChangeFeatured?: boolean;
  onChangeFeatured?: () => void;
}) {
  const self = tree.path[tree.path.length - 1]!;
  const pathIds = new Set(tree.path.map((node) => node.campaign_id));
  // A campaign at the deepest level takes no layer under it (FCM-BE31's TOO_DEEP, always).
  const roomBelow = (node: P2pLayerNodeOut) => node.depth < MAX_LAYER_DEPTH;
  const canAddBelow = writable && roomBelow(self);
  // One request at a time, and a campaign has one parent: no "Add a layer above" while one waits.
  const canAddAbove = writable && tree.path.length === 1 && pendingParent === null;
  // What the viewer opened, closed, unfolded or paged by hand; the read's default expansion stands
  // everywhere else.
  const [toggled, setToggled] = useState<Record<string, boolean>>({});
  const [pages, setPages] = useState<Record<string, number>>({});
  const [unfolded, setUnfolded] = useState<Record<string, boolean>>({});
  const requests = Object.entries(pages).flatMap(([nodeId, count]) =>
    Array.from({ length: count }, (_, page) => ({ nodeId, offset: page * LAYER_PAGE })),
  );
  const fetched = useQueries({
    queries: requests.map((request) => ({
      ...layerNodePageOptions(campaignId, request.nodeId, request.offset),
      enabled,
      retry: false,
    })),
  });
  // Each node's layers in the server's order: what the structure read carried (the way down and its
  // default expansion), then the pages read by hand.
  const known = new Map<string, P2pLayerNodeOut[]>();
  const put = (node: P2pLayerNodeOut) => {
    if (!node.parent_campaign_id) return;
    const list = known.get(node.parent_campaign_id) ?? [];
    if (!list.some((other) => other.campaign_id === node.campaign_id)) list.push(node);
    known.set(node.parent_campaign_id, list);
  };
  tree.path.forEach(put);
  tree.rows.forEach(put);
  requests.forEach((_, index) => fetched[index]?.data?.forEach(put));
  const openedByRead = new Set(tree.rows.map((node) => node.parent_campaign_id));
  const toggleable = (node: P2pLayerNodeOut) =>
    !pathIds.has(node.campaign_id) && (node.layer_count ?? 0) > 0;
  const isOpen = (node: P2pLayerNodeOut) =>
    pathIds.has(node.campaign_id) ||
    (toggleable(node) && (toggled[node.campaign_id] ?? openedByRead.has(node.campaign_id)));

  // The tree, flattened into its lines, each knowing what it draws.
  const lines: Line[] = [];
  function walk(
    node: P2pLayerNodeOut,
    depth: number,
    rails: boolean[],
    last: boolean,
    parent: P2pLayerNodeOut | null,
  ) {
    const line: Line = { kind: 'node', node, depth, rails, last, parent, shape: 'leaf' };
    lines.push(line);
    if (!isOpen(node)) {
      if (toggleable(node)) line.shape = 'toggle';
      return;
    }
    const below = depth === 1 ? [] : [...rails, !last];
    const onPath = pathIds.has(node.campaign_id) && node.campaign_id !== self.campaign_id;
    const all = known.get(node.campaign_id) ?? [];
    let shown: P2pLayerNodeOut[];
    let tail: Line | null = null;
    if (onPath && !unfolded[node.campaign_id]) {
      // A campaign above this one: only the way down is open; its other layers fold into one line.
      shown = all.filter((child) => pathIds.has(child.campaign_id));
      const others = (node.layer_count ?? 0) - shown.length;
      if (node.layer_count != null && others > 0)
        tail = { kind: 'fold', parent: node, depth: depth + 1, rails: below, count: others };
    } else {
      shown = all.slice(0, Math.max(1, pages[node.campaign_id] ?? 1) * LAYER_PAGE);
      // The way down stays visible even past the first page.
      for (const child of all)
        if (pathIds.has(child.campaign_id) && !shown.includes(child)) shown.push(child);
      const remaining = (node.layer_count ?? all.length) - shown.length;
      if (remaining > 0)
        tail = { kind: 'more', parent: node, depth: depth + 1, rails: below, remaining };
      else if (node.campaign_id === self.campaign_id && all.length === 0 && canAddBelow)
        tail = { kind: 'empty', depth: depth + 1, rails: below };
    }
    line.shape = toggleable(node) ? 'toggle' : shown.length > 0 || tail ? 'anchor' : 'leaf';
    shown.forEach((child, index) =>
      walk(child, depth + 1, below, index === shown.length - 1 && tail === null, node),
    );
    if (tail) lines.push(tail);
  }
  // FCM-WEB33: under a waiting request the whole tree sits one step in, below the campaign it asked.
  walk(tree.path[0]!, pendingParent ? 2 : 1, [], true, null);
  const first = lines[0];
  if (pendingParent && first?.kind === 'node') first.pendingAbove = true;
  const linesBelow = new Set<string>();
  lines.forEach((line, index) => {
    const next = lines[index + 1];
    if (line.kind === 'node' && next && next.depth === line.depth + 1)
      linesBelow.add(line.node.campaign_id);
  });
  // D-FCM-56: each campaign whose layers the tree draws, read for what its page shows first, so the
  // fundraiser it features carries the marker (a game opened in its season's tree, a game's own tab).
  const drawn = [
    ...new Set(
      lines.flatMap((line) =>
        line.kind === 'node' && line.parent ? [line.parent.campaign_id] : [],
      ),
    ),
  ];
  const showsFirst = useQueries({
    queries: drawn.map((id) => ({
      ...publicCampaignOptions(id),
      enabled: enabled && featuredOn,
      retry: false,
    })),
  });
  const featuredBy = new Map(drawn.map((id, index) => [id, shownFirst(showsFirst[index]?.data)]));
  // A closed campaign freezes everything under it (FCM-BE31's FROZEN): no acts there. So does this one
  // when a campaign above ended it, which the tree may not show (a campaign another organization runs).
  const frozen = new Set<string>(selfFrozen ? [self.campaign_id] : []);
  for (const line of lines)
    if (
      line.kind === 'node' &&
      (line.node.state === 'closed' || (line.parent && frozen.has(line.parent.campaign_id)))
    )
      frozen.add(line.node.campaign_id);
  const isLive = (node: P2pLayerNodeOut) => node.state === 'draft' || node.state === 'active';

  function acts(node: P2pLayerNodeOut, parent: P2pLayerNodeOut | null): RowAction[] {
    if (!canAct) return [];
    // Under a closed campaign the structure is frozen. A closed campaign under an open one can still be
    // removed (F5, P7); adding and moving need it open.
    if (parent && frozen.has(parent.campaign_id)) return [];
    const list: RowAction[] = [];
    const target = { id: node.campaign_id, name: nameOf(node) };
    // This campaign's own acts sit in the card's header; every other campaign's, on its row.
    if (node.campaign_id !== self.campaign_id && node.can_manage && isLive(node)) {
      if (roomBelow(node))
        list.push({
          label: C.addBelow,
          icon: <Icon icon={Plus} size={14} aria-hidden />,
          testId: `layers-add-below-${node.campaign_id}`,
          onSelect: () => onAddBelow(target),
        });
      if (parent === null)
        list.push({
          label: C.addAbove,
          icon: <Icon icon={Plus} size={14} aria-hidden />,
          testId: `layers-add-above-${node.campaign_id}`,
          onSelect: () => onAddAbove(target),
        });
    }
    // Move and Remove are the parent's side only (Q5). Remove takes a layer out of any campaign its
    // organizers run that hasn't ended, a Deactivated top included (the server refuses only an ended
    // one); Move needs both open.
    if (parent && parent.can_manage) {
      const from = { id: parent.campaign_id, name: nameOf(parent) };
      if (isLive(parent) && node.can_manage && isLive(node))
        list.push({
          label: C.moveAction,
          icon: <Icon icon={ArrowRightLeft} size={14} aria-hidden />,
          testId: `layers-move-${node.campaign_id}`,
          onSelect: () => onMove(node, from),
        });
      list.push({
        label: C.removeFrom(nameOf(parent)),
        destructive: true,
        icon: <Icon icon={Trash2} size={14} aria-hidden />,
        testId: `layers-remove-${node.campaign_id}`,
        onSelect: () => onRemove(node, from),
      });
    }
    return list;
  }

  function toggle(node: P2pLayerNodeOut) {
    const next = !isOpen(node);
    setToggled((now) => ({ ...now, [node.campaign_id]: next }));
    // A node opened by hand reads its first page, unless the structure read already carried it.
    if (next && !known.has(node.campaign_id))
      setPages((now) => ({ ...now, [node.campaign_id]: now[node.campaign_id] ?? 1 }));
  }

  return (
    <Card flush testId="layers-card">
      <CardHeader
        className="max-sm:flex-col"
        title={C.layersTitle}
        subtitle={C.structureSubtitle}
        actions={
          canAddAbove || canAddBelow ? (
            // Phone: one act per line, the width of the card, never two and one (tester-5's item 13).
            <div className="flex w-full flex-col gap-8 sm:w-auto sm:flex-row sm:flex-wrap">
              {seasonAct === 'more' && canAddBelow && onSeason && (
                <Button
                  appearance="saas"
                  tone="secondary"
                  size="sm"
                  className={cn(PHONE_HIT, 'whitespace-nowrap max-sm:w-full')}
                  leadingIcon={<Icon icon={CalendarRange} size={14} aria-hidden />}
                  onClick={onSeason}
                  testId="layers-add-games"
                >
                  {SC.addGames}
                </Button>
              )}
              {canAddAbove && (
                <Button
                  appearance="saas"
                  tone="secondary"
                  size="sm"
                  className={cn(PHONE_HIT, 'whitespace-nowrap max-sm:w-full')}
                  leadingIcon={<Icon icon={Plus} size={14} aria-hidden />}
                  onClick={() => onAddAbove({ id: row.id, name: row.name })}
                  testId="layers-add-above"
                >
                  {C.addAbove}
                </Button>
              )}
              {canAddBelow && (
                <Button
                  appearance="saas"
                  tone="secondary"
                  size="sm"
                  className={cn(PHONE_HIT, 'whitespace-nowrap max-sm:w-full')}
                  leadingIcon={<Icon icon={Plus} size={14} aria-hidden />}
                  onClick={() => onAddBelow({ id: row.id, name: row.name })}
                  testId="layers-add-below"
                >
                  {C.addBelow}
                </Button>
              )}
            </div>
          ) : undefined
        }
      />
      {frozenLine && (
        <div className="border-b border-border-subtle px-20 py-12" data-testid="layers-frozen-line">
          <p className="text-s13 leading-h15 text-text-secondary">{frozenLine}</p>
        </div>
      )}
      {viewOnlyLine && (
        <div
          className="border-b border-border-subtle px-20 py-12"
          data-testid="layers-view-only-line"
        >
          <p className="text-s13 leading-h15 text-text-secondary">{viewOnlyLine}</p>
        </div>
      )}
      <div
        className="[--pad:12px] [--step:18px] lg:[--pad:16px] lg:[--step:24px]"
        data-testid="layers-tree"
      >
        <div
          className="hidden h-[40px] items-center gap-x-12 border-b border-border-subtle bg-neutral-50 pr-[var(--pad)] font-sans text-s11 font-w600 uppercase tracking-wide text-text-secondary lg:grid lg:grid-cols-[minmax(0,1fr)_104px_88px_80px_92px_32px]"
          style={{ paddingLeft: at(1, 16) }}
          aria-hidden
        >
          <span>{C.colCampaign}</span>
          <span className="text-right">{C.colRaised}</span>
          <span className="text-right">{C.colDonations}</span>
          <span>{C.colState}</span>
          <span>{C.colPublic}</span>
          <span />
        </div>
        <ul aria-label={C.layersTitle} className="pb-8 pt-4">
          {pendingParent && (
            <li className="relative" aria-level={1}>
              <PendingParentRow request={pendingParent} onWithdraw={onWithdraw} />
            </li>
          )}
          {lines.map((line) => (
            <li
              key={
                line.kind === 'node'
                  ? line.node.campaign_id
                  : `${line.kind}-${'parent' in line ? line.parent.campaign_id : 'self'}`
              }
              className="relative"
              // The levels, for assistive technology: the lines and the indent draw them on screen.
              aria-level={line.kind === 'node' ? line.depth : undefined}
            >
              <Rails line={line} />
              {line.kind === 'node' ? (
                <TreeRow
                  node={line.node}
                  depth={line.depth}
                  shape={line.shape}
                  self={line.node.campaign_id === self.campaign_id}
                  cardLevel={tree.card != null && line.node.campaign_id === tree.card.campaign_id}
                  featured={
                    line.parent !== null &&
                    featuredBy.get(line.parent.campaign_id) === line.node.campaign_id
                  }
                  open={isOpen(line.node)}
                  linesBelow={linesBelow.has(line.node.campaign_id)}
                  onToggle={() => toggle(line.node)}
                  acts={acts(line.node, line.parent)}
                  keptRemove={
                    canAct &&
                    keptBy !== null &&
                    line.parent?.campaign_id === self.campaign_id &&
                    (line.node.donation_count ?? 0) > 0
                      ? () => onRemove(line.node, { id: self.campaign_id, name: nameOf(self) })
                      : null
                  }
                />
              ) : line.kind === 'more' ? (
                <div className="py-4" style={{ paddingLeft: at(line.depth, 16) }}>
                  <button
                    type="button"
                    className="relative min-h-[32px] font-sans text-s13 font-w600 tracking-givmo text-brand-700 underline-offset-2 hover:underline max-lg:min-h-44"
                    onClick={() =>
                      setPages((now) => ({
                        ...now,
                        [line.parent.campaign_id]: (now[line.parent.campaign_id] ?? 1) + 1,
                      }))
                    }
                    {...testIdProp(`layers-more-${line.parent.campaign_id}`)}
                  >
                    {C.showMore(line.remaining)}
                  </button>
                </div>
              ) : line.kind === 'fold' ? (
                <div className="py-4" style={{ paddingLeft: at(line.depth, -8) }}>
                  <button
                    type="button"
                    className="relative flex min-h-[32px] items-center gap-8 font-sans text-s13 font-w500 tracking-givmo text-text-secondary hover:text-text-strong max-lg:min-h-44"
                    onClick={() => {
                      setUnfolded((now) => ({ ...now, [line.parent.campaign_id]: true }));
                      setPages((now) => ({
                        ...now,
                        [line.parent.campaign_id]: now[line.parent.campaign_id] ?? 1,
                      }));
                    }}
                    {...testIdProp(`layers-fold-${line.parent.campaign_id}`)}
                  >
                    <Icon
                      icon={ChevronRight}
                      size={16}
                      aria-hidden
                      className="shrink-0 text-text-secondary"
                    />
                    {C.otherLayers(line.count)}
                  </button>
                </div>
              ) : (
                <div
                  className="pb-12 pr-20 pt-6"
                  style={{ paddingLeft: at(line.depth, 16) }}
                  data-testid="layers-empty"
                >
                  <p className="text-s13 font-w600 leading-[20px] text-text-strong">
                    {C.layersEmptyTitle}
                  </p>
                  <p className="text-s13 leading-h15 text-text-secondary">{C.layersEmptyBody}</p>
                  {seasonAct === 'setup' && onSeason && (
                    <div
                      className="mt-12 flex flex-col items-start gap-8 border-t border-border-subtle pt-12"
                      data-testid="layers-season-setup"
                    >
                      <p className="text-s13 leading-h15 text-text-secondary">{SC.setUpLine}</p>
                      <Button
                        appearance="saas"
                        size="sm"
                        className={PHONE_HIT}
                        leadingIcon={<Icon icon={CalendarRange} size={14} aria-hidden />}
                        onClick={onSeason}
                        testId="layers-set-up-season"
                      >
                        {SC.setUpAct}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      </div>
      {/* Decision 3 (FCM-BE56): which level's card the pages under this campaign show — any level from
          itself up to the top; a lower campaign's own choice wins. None while its own page isn't public. */}
      {tree.card && (
        <div
          className="flex flex-col gap-8 border-t border-border-subtle px-20 py-12 sm:flex-row sm:items-center sm:justify-between sm:gap-16"
          data-testid="layers-card-level"
        >
          <p className="text-s13 leading-h15 text-text-secondary">
            <span className="font-w600 text-text-strong">{C.cardTitle}</span>
            {' · '}
            {C.cardLine(tree.card.campaign_name)}
          </p>
          {/* A top has no level above it to choose. */}
          {canChangeCard && (
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              className={cn('shrink-0 self-start sm:self-auto', PHONE_HIT)}
              onClick={onChangeCard}
              testId="layers-card-level-change"
            >
              {C.cardChange}
            </Button>
          )}
        </div>
      )}
      {featuredLine && (
        <FeaturedLine
          line={featuredLine}
          canChange={canChangeFeatured && onChangeFeatured !== undefined}
          onChange={() => onChangeFeatured?.()}
        />
      )}
      {footnote && (
        <div
          className="border-t border-border-subtle px-20 py-12"
          data-testid="layers-tree-footnote"
        >
          <p className="text-s13 leading-h15 text-text-secondary">{footnote}</p>
        </div>
      )}
    </Card>
  );
}

/** The connector pieces a line draws: the rails above it, and its elbow off its parent's line. */
function Rails({ line }: { line: Line }) {
  const parentDepth = line.depth - 1;
  const last = line.kind === 'node' ? line.last : true;
  // Where the elbow stops: short of a chevron, at an anchor dot, or near a name.
  const reach =
    line.kind === 'node'
      ? line.shape === 'toggle'
        ? -9
        : line.shape === 'anchor'
          ? -4
          : 10
      : line.kind === 'fold'
        ? -9
        : 10;
  // The elbow meets the middle of the line's first row of text.
  const height =
    line.kind === 'node' ? 'h-[22px]' : line.kind === 'empty' ? 'h-[16px]' : 'h-[26px] lg:h-[20px]';
  return (
    <>
      {line.rails.map((on, index) =>
        on ? (
          <span
            key={index}
            aria-hidden
            className={cn('pointer-events-none absolute inset-y-0 z-[1] border-l', LINE)}
            style={{ left: at(index + 1) }}
          />
        ) : null,
      )}
      {parentDepth >= 1 && (
        <>
          {!last && (
            <span
              aria-hidden
              className={cn('pointer-events-none absolute inset-y-0 z-[1] border-l', LINE)}
              style={{ left: at(parentDepth) }}
            />
          )}
          {/* Above the row's own tint, so the line into "This campaign" stays drawn. */}
          <span
            aria-hidden
            className={cn(
              'pointer-events-none absolute top-0 z-[1] border-b border-l',
              LINE,
              height,
              last && 'rounded-bl-[6px]',
              line.kind === 'node' && line.pendingAbove && 'border-dashed',
            )}
            style={{ left: at(parentDepth), width: `calc(var(--step) + ${reach}px)` }}
          />
        </>
      )}
    </>
  );
}

/**
 * FCM-WEB33 (frame 06): the campaign this one asked to join, drawn where it would sit, its line down to
 * this campaign dashed (nothing is linked until its organizers approve), with the way to withdraw.
 */
function PendingParentRow({
  request,
  onWithdraw,
}: {
  request: P2pOwnLayerRequestOut;
  onWithdraw: ((request: P2pOwnLayerRequestOut) => void) | null;
}) {
  const parent = request.parent_campaign_name ?? C.anotherCampaign;
  const organization = request.parent_organization_name;
  return (
    <div
      className="relative py-12 pr-[var(--pad)] font-sans text-s14 tracking-givmo text-text"
      style={{ paddingLeft: at(1, 16) }}
      data-testid="layers-pending-parent-row"
    >
      <span
        aria-hidden
        className={cn('pointer-events-none absolute bottom-0 border-l border-dashed', LINE)}
        style={{ left: at(1), top: MID + 3 }}
      />
      <span
        aria-hidden
        className="pointer-events-none absolute size-[7px] -translate-x-[3px] -translate-y-1/2 rounded-full border border-icon-muted bg-white"
        style={{ left: at(1), top: MID }}
      />
      <div className="flex min-w-0 flex-col gap-4 leading-[20px]">
        <span className="flex min-w-0 flex-wrap items-center gap-x-8 gap-y-4">
          <span className="font-w600 text-text">{parent}</span>
          <Badge tone="neutral">{R.pendingBadge}</Badge>
        </span>
        <span className="block text-s12 leading-h15 text-text-secondary">
          {organization ? R.runBy(organization) : C.runBy('organization')} ·{' '}
          {R.pendingLine(displayP2pDate(request.created_at), displayP2pDate(request.expires_at))}
        </span>
        <span className="block text-s13 leading-h15 text-text-secondary">
          {R.pendingNote(parent)}
        </span>
        {onWithdraw && (
          <div className="mt-4">
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              className={PHONE_HIT}
              onClick={() => onWithdraw(request)}
              testId="layers-withdraw-request"
            >
              {R.withdraw}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function TreeRow({
  node,
  depth,
  shape,
  self,
  cardLevel,
  featured = false,
  open,
  linesBelow,
  onToggle,
  acts,
  keptRemove,
}: {
  node: P2pLayerNodeOut;
  depth: number;
  shape: Shape;
  self: boolean;
  /** Decision 3: the campaign whose card the pages under this one show. */
  cardLevel: boolean;
  /** D-FCM-56: the fundraiser its game's page shows first. */
  featured?: boolean;
  open: boolean;
  linesBelow: boolean;
  onToggle: () => void;
  acts: RowAction[];
  /** A top its funded layers keep: this funded layer shows Remove itself, not behind the menu (08b). */
  keptRemove: (() => void) | null;
}) {
  // Its figures, state and layers ride only where the viewer's organization runs it or a campaign above
  // it (Q4); elsewhere it is a name only.
  const seen = node.raised_amount != null;
  const name = nameOf(node);
  const money = seen ? displayP2pMoney(node.raised_amount ?? '0.00') : null;
  const menu =
    acts.length > 0 && !keptRemove ? (
      <RowActions
        label={C.rowActions(name)}
        contentClassName="fundraisers-menu"
        testId={`layers-actions-${node.campaign_id}`}
        actions={acts}
      />
    ) : null;
  const removeVisible = keptRemove ? (
    <Button
      appearance="saas"
      tone="secondary"
      size="sm"
      className={PHONE_HIT}
      leadingIcon={<Icon icon={Trash2} size={14} aria-hidden />}
      onClick={keptRemove}
      testId={`layers-remove-visible-${node.campaign_id}`}
    >
      {C.removeVisible}
    </Button>
  ) : null;
  // A closed node's count rides the word before it, so it is never alone on a line; a name keeps its
  // day's month and number together (tester-5's items 12 and 13).
  const count =
    shape === 'toggle' && !open ? (
      <span
        className="ml-4 whitespace-nowrap text-s13 text-text-tertiary"
        data-testid={`layers-count-${node.campaign_id}`}
      >
        {C.layerCount(node.layer_count ?? 0)}
      </span>
    ) : null;
  const last = featured ? 'featured' : cardLevel ? 'card' : self ? 'self' : 'name';
  const withCount = (at: typeof last, part: ReactNode) =>
    count && at === last ? (
      <span className={at === 'name' ? 'min-w-0' : 'whitespace-nowrap'}>
        {part}
        {'\u00a0'}
        {count}
      </span>
    ) : (
      part
    );
  const title = (
    <span className="flex min-w-0 flex-wrap items-baseline gap-x-8">
      {/* A campaign this organization runs links, whatever this person's rank on it. */}
      {withCount(
        'name',
        self || !node.ours || node.campaign_name == null ? (
          <span className={cn('font-w600', self ? 'text-text-strong' : 'text-text')}>
            {keepDayTogether(name)}
          </span>
        ) : (
          <ModuleLink
            href={FUNDRAISERS_ROUTES.editor(node.campaign_id)}
            testId={`layers-open-${node.campaign_id}`}
            className="relative text-left font-w600 text-brand-700 underline-offset-2 hover:underline after:absolute after:inset-x-0 after:-inset-y-2 max-lg:after:-inset-y-12"
          >
            {keepDayTogether(name)}
          </ModuleLink>
        ),
      )}
      {self &&
        withCount(
          'self',
          <span
            className="text-s12 font-w600 tracking-givmo text-brand-700"
            data-testid="layers-this-campaign"
          >
            {C.thisCampaign}
          </span>,
        )}
      {cardLevel &&
        withCount(
          'card',
          <span
            className="inline-flex items-center gap-4 text-s12 font-w500 tracking-givmo text-text-secondary"
            data-testid="layers-card-level-marker"
          >
            <Icon icon={IdCard} size={13} aria-hidden className="shrink-0" />
            {C.cardMarker}
          </span>,
        )}
      {featured &&
        withCount(
          'featured',
          <span
            className="inline-flex items-center gap-4 text-s12 font-w500 tracking-givmo text-text-secondary"
            data-testid={`layers-featured-marker-${node.campaign_id}`}
          >
            <Icon icon={Star} size={13} aria-hidden className="shrink-0" />
            {SC.featuredMarker}
          </span>,
        )}
    </span>
  );
  const notes = (
    <>
      {!node.ours && seen && (
        <span
          className="block text-s12 leading-h15 text-text-secondary"
          data-testid={`layers-run-by-${node.campaign_id}`}
        >
          {C.runBy(node.owner_kind)}
        </span>
      )}
      {keptRemove && (
        <span className="block text-s12 leading-h15 text-text-secondary">{C.fundedHint}</span>
      )}
    </>
  );
  const state = node.state ? (
    <Badge tone={fundraiserStateTone(node.state)}>{fundraiserStateLabel(node.state)}</Badge>
  ) : null;
  return (
    <div
      className={cn(
        'relative py-12 pr-[var(--pad)] font-sans text-s14 tracking-givmo text-text',
        self && 'bg-surface-active',
      )}
      style={{ paddingLeft: at(depth, 16) }}
      data-testid={`layers-row-${node.campaign_id}`}
      data-depth={depth}
      {...(self ? { 'aria-current': 'page' as const } : {})}
    >
      {linesBelow && (
        <span
          aria-hidden
          className={cn('pointer-events-none absolute bottom-0 border-l', LINE)}
          style={{ left: at(depth), top: shape === 'toggle' ? MID + 9 : MID + 3 }}
        />
      )}
      {shape === 'anchor' && (
        <span
          aria-hidden
          className={cn(
            'pointer-events-none absolute size-[7px] -translate-x-[3px] -translate-y-1/2 rounded-full',
            self ? 'bg-brand-500' : 'bg-icon-muted',
          )}
          style={{ left: at(depth), top: MID }}
        />
      )}
      {shape === 'toggle' && (
        <button
          type="button"
          aria-expanded={open}
          aria-label={open ? C.hideLayers(name) : C.showLayers(name)}
          onClick={onToggle}
          className="absolute flex h-24 w-24 -translate-x-full -translate-y-1/2 items-center justify-end pr-4 text-text-secondary hover:text-text-strong max-lg:after:absolute max-lg:after:-inset-10"
          style={{ left: at(depth, 12), top: MID }}
          {...testIdProp(`layers-toggle-${node.campaign_id}`)}
        >
          <Icon
            icon={ChevronRight}
            size={16}
            aria-hidden
            className={cn('transition-transform', open && 'rotate-90')}
          />
        </button>
      )}
      {/* Desktop: the tree's columns. */}
      <div className="hidden items-start gap-x-12 lg:grid lg:grid-cols-[minmax(0,1fr)_104px_88px_80px_92px_32px]">
        <div className="min-w-0 leading-[20px]">
          {title}
          {notes}
          {removeVisible && <div className="mt-8">{removeVisible}</div>}
        </div>
        <span className="text-right tabular-nums leading-[20px]">
          {money}
          {seen && node.show_statistics === false && (
            <span
              className="block text-s12 leading-h15 text-text-secondary"
              data-testid={`layers-hidden-publicly-${node.campaign_id}`}
            >
              {C.hiddenPublicly}
            </span>
          )}
        </span>
        <span className="text-right tabular-nums leading-[20px]">
          {seen ? String(node.donation_count ?? 0) : null}
        </span>
        <span className="leading-[20px]">{state}</span>
        <span
          className={cn(
            'leading-[20px]',
            node.publicly_servable ? 'text-text-strong' : 'text-text-secondary',
          )}
        >
          {seen ? (node.publicly_servable ? C.publicLive : C.publicOff) : null}
        </span>
        <span className="-my-6 flex justify-end">{menu}</span>
      </div>
      {/* Phone: the name and its state on one line, the figures beneath. */}
      <div className="flex items-start gap-4 lg:hidden">
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-8">
            <div className="min-w-0 leading-[20px]">{title}</div>
            {state && <span className="shrink-0">{state}</span>}
          </div>
          {notes}
          {seen && (
            <p className="mt-4 text-s13 leading-h15 tabular-nums text-text-secondary">
              <span className="font-w600 text-text-strong">{money}</span>
              {node.show_statistics === false && ` · ${C.hiddenPublicly}`}
              {` · ${C.donations(node.donation_count ?? 0)} · ${
                node.publicly_servable ? C.publicLive : C.publicOff
              }`}
            </p>
          )}
          {removeVisible && <div className="mt-8">{removeVisible}</div>}
        </div>
        {/* The menu's place is kept on a row without one, so the state badges line up. */}
        {menu ? (
          <div className="-my-12 -mr-8 shrink-0">{menu}</div>
        ) : (
          <div aria-hidden className="w-[36px] shrink-0" />
        )}
      </div>
    </div>
  );
}
